import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  title?: string;
  duration?: number;
  action?: ToastAction;
}

export interface ToastOptions {
  type: Toast["type"];
  title?: string;
  message: string;
  duration?: number;
  action?: ToastAction;
}

export interface ShowToast {
  (options: ToastOptions): string;
  (message: string, type?: Toast["type"], duration?: number): string;
}

interface ToastState {
  toasts: Toast[];
  showToast: ShowToast;
  dismissToast: (id: string) => void;
  clearAll: () => void;
}

const DEFAULT_DURATION = 3000;
const VISIBLE_TOAST_LIMIT = 3;

export const useToastStore = create<ToastState>()(
  devtools(
    (set, get) => {
      const timers = new Map<
        string,
        { handle: ReturnType<typeof setTimeout> | null; remaining: number; startedAt: number }
      >();

      const syncVisibleTimers = () => {
        const { toasts } = get();
        const visibleIds = new Set(toasts.slice(-VISIBLE_TOAST_LIMIT).map((toast) => toast.id));
        for (const toast of toasts) {
          if (!toast.duration || toast.duration <= 0) continue;
          const timer = timers.get(toast.id) ?? {
            handle: null,
            remaining: toast.duration,
            startedAt: 0,
          };
          timers.set(toast.id, timer);
          if (visibleIds.has(toast.id) && !timer.handle) {
            timer.startedAt = Date.now();
            timer.handle = setTimeout(() => get().dismissToast(toast.id), timer.remaining);
          } else if (!visibleIds.has(toast.id) && timer.handle) {
            clearTimeout(timer.handle);
            timer.handle = null;
            timer.remaining -= Date.now() - timer.startedAt;
          }
        }
      };

      const showToast = ((
        messageOrOptions: string | ToastOptions,
        type: Toast["type"] = "info",
        duration: number = DEFAULT_DURATION,
      ): string => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const toast: Toast =
          typeof messageOrOptions === "string"
            ? { id, message: messageOrOptions, type, duration }
            : {
                id,
                message: messageOrOptions.message,
                type: messageOrOptions.type,
                title: messageOrOptions.title,
                duration: messageOrOptions.duration ?? DEFAULT_DURATION,
                action: messageOrOptions.action,
              };

        set((state) => ({ toasts: [...state.toasts, toast] }));
        syncVisibleTimers();

        return id;
      }) as ShowToast;

      return {
        toasts: [],
        showToast,
        dismissToast: (id) => {
          const timer = timers.get(id);
          if (timer?.handle) clearTimeout(timer.handle);
          timers.delete(id);
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }));
          syncVisibleTimers();
        },
        clearAll: () => {
          for (const timer of timers.values()) if (timer.handle) clearTimeout(timer.handle);
          timers.clear();
          set({ toasts: [] });
        },
      };
    },
    { name: "ToastStore" },
  ),
);
